import { Component, computed, inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { prefix } from '@validators';
import { Attachment } from '@models';
import { AttachmentService, NotificationService } from '@services';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { disabled, form, FormField, required, submit, validate } from '@angular/forms/signals';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-dataset-attachment',
  templateUrl: './attachment.component.html',
  styleUrls: ['./attachment.component.scss'],
  imports: [
    FormField,
    DatePipe,
    MatInputModule,
    MatFormFieldModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatProgressBarModule,
  ],
})
export class DatasetFormAttachmentComponent {
  dialogRef = inject<MatDialogRef<DatasetFormAttachmentComponent>>(MatDialogRef);
  attachments = inject<Attachment[]>(MAT_DIALOG_DATA);

  attachmentService = inject(AttachmentService);
  notificationService = inject(NotificationService);

  isLoading = signal(false);
  isBulkEdit = signal(this.attachments.length > 1);

  maxReleaseDate = computed(() => {
    let date = new Date();
    for (const attachment of this.attachments) {
      const created = new Date(attachment.created);
      if (created < date) {
        date = created;
      }
    }

    date.setFullYear(date.getFullYear() + 2);
    return date;
  });

  attachmentForm = form(signal<{
    filename: string;
    prefix: string;
    title: string;
    description: string;
    releaseDate: string;
  }>({
    filename: this.attachments[0]?.filename || "",
    prefix: this.isBulkEdit() ? "" : this.attachments[0]?.prefix,
    title: this.attachments[0]?.title || "",
    description: this.attachments[0]?.description || "",
    releaseDate: this.attachments[0]?.released || "",
  }), (schemaPath) => {
    required(schemaPath.filename);
    required(schemaPath.prefix, {
      when: () => {
        // required when dirty in bulk edit
        return !!this.attachments.length && this.attachmentForm.prefix().dirty();
      }
    });

    disabled(schemaPath.filename, { when: this.isBulkEdit });
    disabled(schemaPath.description, { when: this.isBulkEdit });
    disabled(schemaPath.title, { when: this.isBulkEdit });

    prefix(schemaPath.prefix);

    validate(schemaPath.releaseDate, ({ value }) => {
      if (this.isBulkEdit() && !this.attachmentForm.releaseDate().dirty()) {
        return null;
      }

      const v = value();
      if (!v) {
        return null;
      }

      const selectedDate = new Date(v);
      if (selectedDate > this.maxReleaseDate()) {
        return {
          kind: 'latestReleaseDateExceeded',
        };
      }

      return null;
    });
  });

  public async submit(event: Event) {
    event.preventDefault();

    submit(this.attachmentForm, async () => {
      this.isLoading.set(true);

      const filename = this.attachmentForm.filename();
      const title = this.attachmentForm.title();
      const description = this.attachmentForm.description();
      const prefix = this.attachmentForm.prefix();
      const releaseDate = this.attachmentForm.releaseDate();

      const updatedAttachments = [];
      for (const attachment of this.attachments) {
        try {
          const updatedAttachment = await this.attachmentService.updateAttachment(
            attachment.datasetId,
            attachment.id,
            {
              recordId: attachment.recordId,
              parentId: attachment.parentId,
              filename: filename.disabled() ? attachment.filename : filename.value(),
              title: title.disabled() ? attachment.title : title.value(),
              description: description.disabled() ? attachment.description : description.value(),
              prefix: prefix.dirty() ? prefix.value() : attachment.prefix,
              released: releaseDate.dirty() ? releaseDate.value() : attachment.released,
            } as Attachment,
          );

          updatedAttachments.push(updatedAttachment);
        } catch (err) {
          console.error(err);
        }
      }

      if (updatedAttachments.length != this.attachments.length) {
        this.notificationService.error('Failed to update one or multiple files.');
      }

      this.dialogRef.close(updatedAttachments);
    });
  }
}
